import { ICollapsibleMenuActionItemParams } from '@components/common/CollapsibleActionsMenu/CollapsibleActionsMenu';

import { IVisibleShowHideAssetsResponse, TAsset } from '@interfaces/assets/asset';

import { EAssetVisibilityActions, EAssetVisibilityActionsText, TAssetVisibilityActions } from './constants';

const ACTIONS_LIST: TAssetVisibilityActions[] = [
	EAssetVisibilityActions.hideAll,
	EAssetVisibilityActions.hideAllFiltered,
	EAssetVisibilityActions.hideCurrentPage,
	EAssetVisibilityActions.hideSelected];

export const getAssetIdsForAction = (
	actionId: string,
	pageRows: TAsset[],
	selectedRows: TAsset[],
): string[] => {
	switch (actionId) {
		case EAssetVisibilityActions.showSelected:
		case EAssetVisibilityActions.hideSelected:
			return selectedRows.map(row => row.id);
		case EAssetVisibilityActions.showCurrentPage:
		case EAssetVisibilityActions.hideCurrentPage:
			return pageRows.map(row => row.id);
		default:
			return [];
	}
};

export const checkRowsVisibility = (rows: TAsset[]) => {
	const visibleRows = rows.filter((row) => row.showOnMap);

	return {
		availableToShow: visibleRows.length !== rows.length && !!rows.length,
		availableToHide: !!visibleRows.length && visibleRows.length <= rows.length && !!rows.length,
	};
};

export const getAssetVisibilityActionOptions = (
	{
		rows,
		selectedRows,
		visibleByFilter,
		visibleOnAll,
		isDirtyFilters,
	}: {
		rows: TAsset[];
		selectedRows: TAsset[];
		visibleByFilter?: IVisibleShowHideAssetsResponse;
		visibleOnAll?: IVisibleShowHideAssetsResponse;
		isDirtyFilters?: boolean;
	},
): ICollapsibleMenuActionItemParams<TAssetVisibilityActions>[] => {
	const currentPageAssetsVisibility = checkRowsVisibility(rows);
	const selectedAssetsVisibility = checkRowsVisibility(selectedRows);

	return [
		{
			label: 'Show on the map',
			name: 'show',
			items: [
				{
					text: EAssetVisibilityActionsText.selectedOnCurrentPage,
					id: EAssetVisibilityActions.showSelected,
					disabled: !selectedAssetsVisibility.availableToShow,
				},
				{
					text: EAssetVisibilityActionsText.currentPage,
					id: EAssetVisibilityActions.showCurrentPage,
					disabled: !currentPageAssetsVisibility.availableToShow,
				},
				{
					text: EAssetVisibilityActionsText.allFiltered,
					id: EAssetVisibilityActions.showAllFiltered,
					disabled: !visibleByFilter?.isShowVisible || !isDirtyFilters,
				},
				{
					text: EAssetVisibilityActionsText.all,
					id: EAssetVisibilityActions.showAll,
					disabled: !visibleOnAll?.isShowVisible,
				},
			],
		},
		{
			label: 'Hide on the map',
			name: 'hide',
			items: [
				{
					text: EAssetVisibilityActionsText.selectedOnCurrentPage,
					id: EAssetVisibilityActions.hideSelected,
					disabled: !selectedAssetsVisibility.availableToHide,
				},
				{
					text: EAssetVisibilityActionsText.currentPage,
					id: EAssetVisibilityActions.hideCurrentPage,
					disabled: !currentPageAssetsVisibility.availableToHide,
				},
				{
					text: EAssetVisibilityActionsText.allFiltered,
					id: EAssetVisibilityActions.hideAllFiltered,
					disabled: !visibleByFilter?.isHideVisible || !isDirtyFilters,
				},
				{
					text: EAssetVisibilityActionsText.all,
					id: EAssetVisibilityActions.hideAll,
					disabled: !visibleOnAll?.isHideVisible,
				},
			],
		},
	];
};

export const getActionsAttributes = (actionId: TAssetVisibilityActions) => {
	const isHide = ACTIONS_LIST.includes(actionId);

	const isAllAssets = actionId === EAssetVisibilityActions.showAll || actionId === EAssetVisibilityActions.hideAll;
	const isFilter = (
		actionId === EAssetVisibilityActions.showAllFiltered
		|| actionId === EAssetVisibilityActions.hideAllFiltered
	);

	return {
		isFilter,
		isAllAssets,
		isHide,
		isAssetIds: !isAllAssets && !isFilter,
	};
};

export const getConfirmModalQuestionText = (isHide: boolean) => {
	return `Are you sure you want to ${isHide ? 'hide' : 'show'} selected assets on the map?`;
};
